export const minimumQuantityErrorKey = 'order.item.minimumQuantity.notMet';
export const quantityIncrementErrorKey = 'order.item.quantityIncrement.notMet';

export interface QuantityValidationError {
  readonly message: string;
  readonly values: {
    readonly quantity: number;
    readonly minimumQuantity: number | null;
    readonly quantityIncrement: number | null;
  };
}

interface QuantityProduct {
  readonly saleMinimumQuantity: number | null;
  readonly saleQuantityIncrements: number | null;
}

export interface QuantityEntry {
  readonly quantity: number;
  readonly product: QuantityProduct;
  readonly quantityValidationError?: QuantityValidationError;
}

const quantityErrorTranslationKeys: Readonly<Record<string, string>> = {
  [minimumQuantityErrorKey]: 'minimumQuantityNotMet',
  [quantityIncrementErrorKey]: 'quantityIncrementNotMet'
};

export const isQuantityValidationError = (message: string): boolean =>
  message in quantityErrorTranslationKeys;

// Maps a backend error message to its translation key, so locale files keep their own naming.
export const getQuantityErrorTranslationKey = (message: string): string | null =>
  quantityErrorTranslationKeys[message] ?? null;

export const getQuantityValidationError = ({
  quantity,
  product: { saleMinimumQuantity: minimumQuantity, saleQuantityIncrements: quantityIncrement },
  quantityValidationError
}: QuantityEntry): QuantityValidationError | null => {
  let error: QuantityValidationError | null = null;

  if (
    quantityValidationError !== undefined &&
    quantityValidationError.values.quantity === quantity
  ) {
    error = quantityValidationError;
  } else if (minimumQuantity !== null && quantity < minimumQuantity) {
    error = {
      message: minimumQuantityErrorKey,
      values: { quantity, minimumQuantity, quantityIncrement }
    };
  } else if (
    quantityIncrement !== null &&
    quantityIncrement > 0 &&
    (quantity - (minimumQuantity ?? 0)) % quantityIncrement !== 0
  ) {
    error = {
      message: quantityIncrementErrorKey,
      values: { quantity, minimumQuantity, quantityIncrement }
    };
  }

  return error;
};

export const hasInvalidCartQuantities = (cart: {
  readonly entries: ReadonlyArray<QuantityEntry>;
}): boolean => cart.entries.some((entry) => getQuantityValidationError(entry) !== null);

export const getFirstOrderableQuantity = (
  minimumQuantity: number | null,
  quantityIncrement: number | null
): number => {
  const base = minimumQuantity ?? 0;
  const increment = quantityIncrement !== null && quantityIncrement > 0 ? quantityIncrement : 1;
  return base >= 1 ? base : base + Math.ceil((1 - base) / increment) * increment;
};
